/**
 * Agent Service - Frontend service for agent conversation operations
 */

import api from "../../../shared/api.jsx";

/**
 * Get all conversations for admin dashboard
 * @param {Object} filters - Filter criteria
 * @param {string} [filters.status] - Filter by status (pending, active, closed)
 * @param {string} [filters.agentId] - Filter by agent ID
 * @param {number} [filters.limit] - Limit results
 * @param {number} [filters.offset] - Offset for pagination
 * @returns {Promise<Object>} Conversations data
 */
export const getConversations = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.status) {
      params.append("status", filters.status);
    }
    if (filters.agentId) {
      params.append("agentId", filters.agentId);
    }
    if (filters.limit) {
      params.append("limit", filters.limit.toString());
    }
    if (filters.offset) {
      params.append("offset", filters.offset.toString());
    }

    const response = await api.get(`/agent/conversations?${params.toString()}`);
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get conversation with messages
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Conversation with messages
 */
export const getConversation = async (conversationId) => {
  try {
    const response = await api.get(
      `/agent/conversations/${conversationId}/messages`
    );
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Send agent message to customer
 * @param {string} conversationId - Conversation ID
 * @param {Object} messageData - Message data
 * @param {string} messageData.content - Message content
 * @param {string} messageData.agentId - Agent ID
 * @param {string} [messageData.messageType] - Message type (default: text)
 * @returns {Promise<Object>} Message sending result
 */
export const sendAgentMessage = async (conversationId, messageData) => {
  try {
    const response = await api.post(
      `/agent/conversations/${conversationId}/messages`,
      messageData
    );
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Close conversation
 * @param {string} conversationId - Conversation ID
 * @param {string} closedBy - Who closed the conversation
 * @returns {Promise<Object>} Close result
 */
export const closeConversation = async (conversationId, closedBy) => {
  try {
    const response = await api.put(
      `/agent/conversations/${conversationId}/close`,
      { closedBy }
    );
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Assign conversation to agent
 * @param {string} conversationId - Conversation ID
 * @param {string} agentId - Agent ID
 * @returns {Promise<Object>} Assignment result
 */
export const assignConversation = async (conversationId, agentId) => {
  try {
    const response = await api.put(
      `/agent/conversations/${conversationId}/assign`,
      { agentId }
    );
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get agent conversation statistics
 * @returns {Promise<Object>} Statistics
 */
export const getAgentStats = async () => {
  try {
    const response = await api.get("/agent/stats");
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};
